import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { Box, Skeleton, Typography } from "@mui/material";
import {
  CreditCardRounded,
  AccountBalanceRounded,
  CreditCardOffRounded,
} from "@mui/icons-material";
import { BaseModal } from "./BaseModal";
import { PatientSummaryCard } from "./PatientSummaryCard";
import { useStateAuthValue } from "../../context/AuthState";
import { getPaymentMethod } from "../../services/PaymentService";

// Modal de solo lectura: muestra el método de pago del socio (tarjeta o CBU).
// La edición está deshabilitada en toda la app, por eso es un visor, no un form.
export const PaymentDetailsModals = ({ open, setOpen, patient }) => {
  const [{ userToken }] = useStateAuthValue();
  const [paymentData, setPaymentData] = useState(null);
  const [loadingPaymentData, setLoadingPaymentData] = useState(true);

  useEffect(() => {
    const GetPaymentMethod = async () => {
      setLoadingPaymentData(true);
      await getPaymentMethod(userToken, patient.email, patient.product)
        .then((res) => {
          setPaymentData(res);
        })
        .catch(() => {
          setPaymentData(null);
        })
        .finally(() => {
          setLoadingPaymentData(false);
        });
    };
    GetPaymentMethod();
  }, []);

  const isCard = Boolean(paymentData?.tarjeta_numero);
  const isCbu = Boolean(paymentData?.cbu);

  const maskCardNumber = (str) => {
    const pattern =
      /^[A-Za-z0-9]{4} [A-Za-z0-9]{4} [A-Za-z0-9]{4} [A-Za-z0-9]{4}$/;
    if (str && pattern.test(str)) {
      const firstQuartet = str.slice(0, 7);
      const lastFourDigits = str.slice(-4);
      return `${firstQuartet} ** **** ${lastFourDigits}`;
    }
    return str;
  };

  // Fila etiqueta/valor para los datos del método de pago.
  const DataRow = ({ label, value, mono }) => (
    <Box>
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
          color: "#8a929c",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 600,
          color: "#002C54",
          fontFamily: mono ? "monospace" : "inherit",
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );

  const renderPaymentMethod = () => {
    if (isCard) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Visual tipo tarjeta */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #002C54, #003f77)",
              borderRadius: "16px",
              color: "white",
              padding: "1.5rem",
              boxShadow: "0 8px 24px rgba(0,44,84,0.25)",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
              minHeight: 150,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, opacity: 0.85 }}
              >
                Tarjeta de crédito / débito
              </Typography>
              <CreditCardRounded />
            </Box>
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "20px",
                letterSpacing: "2px",
                fontWeight: 600,
              }}
            >
              {maskCardNumber(paymentData?.tarjeta_numero)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: "10px", opacity: 0.7 }}>
                  TITULAR
                </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {paymentData?.tarjeta_titular || "—"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: "10px", opacity: 0.7 }}>
                  VENCE
                </Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                  {paymentData?.tarjeta_fecha_vencimiento || "—"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    if (isCbu) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            background: "#f6f8fa",
            border: "1px solid #e8ebef",
            borderRadius: "14px",
            padding: "1.25rem",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              background: "rgba(0,44,84,0.1)",
              color: "#002C54",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AccountBalanceRounded />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.6fr 1fr",
              gap: "1rem",
              flexGrow: 1,
            }}
          >
            <DataRow label="CBU" value={paymentData?.cbu} mono />
            <DataRow label="Titular" value={paymentData?.cbu_titular} />
          </Box>
        </Box>
      );
    }

    // Sin método de pago
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.75rem",
          padding: "2rem",
          background: "#f6f8fa",
          border: "1px dashed #d4d8dd",
          borderRadius: "14px",
          color: "#8a929c",
        }}
      >
        <CreditCardOffRounded sx={{ fontSize: 36 }} />
        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
          El socio no posee método de pago registrado
        </Typography>
      </Box>
    );
  };

  return (
    <BaseModal width="600px" open={open} setOpen={setOpen}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {/* Header de marca (azul) — vista informativa */}
        <Box
          sx={{
            background: "linear-gradient(195deg, #002C54, #003f77)",
            borderRadius: "15px",
            color: "white",
            padding: "1.25rem 1.5rem",
            boxShadow: "0 8px 24px rgba(0,44,84,0.25)",
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CreditCardRounded />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
              Método de pago
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.85 }}>
              Datos de cobro del socio
            </Typography>
          </Box>
        </Box>

        <PatientSummaryCard patient={patient} />

        {loadingPaymentData ? (
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: "16px" }} />
        ) : (
          renderPaymentMethod()
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              paddingX: "1.5rem",
              color: "#5a6470",
              borderColor: "#d4d8dd",
              "&:hover": { borderColor: "#b9bfc6", background: "#f5f6f8" },
            }}
          >
            Cerrar
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};
